import { posthog } from "@/lib/posthogClient";
import type { CaptureResult } from "posthog-js";

// Relato de crash do dashboard. Vai pelo PostHog porque o browser não pode
// falar com o Discord: a URL do webhook é segredo e tudo que está em src vai
// para o bundle público.
//
// O evento é tratado como público: a mensagem de erro é texto livre e a URL
// carrega id, token de link mágico etc. Por isso tudo passa por `scrub`, a
// stack perde o cabeçalho (que repete a mensagem crua) e as propriedades que
// o posthog-js somaria com a URL completa são sobrescritas aqui ou, as que ele
// soma depois das nossas, removidas em `stripBoundaryEventUrls`.

export const BOUNDARY_ERROR_EVENT = "dashboard_error_boundary";

const MAX_MESSAGE_LENGTH = 300;
const MAX_STACK_LENGTH = 900;

// `posthog.capture` antes do `init` é descartado em silêncio, e o
// PostHogProvider só inicializa em useEffect, depois do commit: sem esperar,
// um crash no primeiro render se perderia. Se não inicializar (localhost ou
// bloqueador), desiste: o console.error do ErrorBoundary continua valendo.
const INIT_POLL_INTERVAL_MS = 250;
const INIT_POLL_MAX_ATTEMPTS = 20;

const REDACTIONS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\beyJ[\w-]+\.[\w-]+(?:\.[\w-]+)?/g, "[token]"],
  [/\bBearer\s+\S+/gi, "[token]"],
  [
    /\b(access_token|refresh_token|token|code|password|senha|secret|authorization|api[_-]?key)\b\s*[=:]\s*\S+/gi,
    "$1=[redacted]",
  ],
  [/(https?:\/\/[^\s?#)]+)[?#][^\s)]*/g, "$1"],
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]"],
  [/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, "[documento]"],
  [/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, "[documento]"],
  [/\(?\b\d{2}\)?[\s-]?\d{4,5}-\d{4}\b/g, "[telefone]"],
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "[id]"],
  [/\b\d{8,}\b/g, "[numero]"],
  [/\b[A-Za-z0-9_-]{32,}\b/g, "[token]"],
];

const scrub = (value: string): string =>
  REDACTIONS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value
  );

const isStackFrame = (line: string) =>
  /^\s*at\s/.test(line) || /@.*:\d+:\d+\s*$/.test(line);

const sanitizeStack = (stack: string | null | undefined): string => {
  const frames = (stack ?? "").split("\n").filter(isStackFrame);
  if (frames.length === 0) return "n/a";
  return scrub(frames.join("\n")).slice(0, MAX_STACK_LENGTH);
};

const sanitizePathSegment = (segment: string) => {
  if (/^v\d+$/.test(segment)) return segment;
  return /[\d@%]/.test(segment) || segment.length > 24 ? ":id" : segment;
};

const sanitizedPathname = () =>
  window.location.pathname.split("/").map(sanitizePathSegment).join("/");

const errorField = (error: unknown, field: "name" | "message" | "stack") => {
  if (typeof error !== "object" || error === null) return undefined;
  const value = (error as Record<string, unknown>)[field];
  return typeof value === "string" ? value : undefined;
};

type ComponentInfo = { componentStack?: string | null };

export const sanitizeBoundaryError = (error: unknown, info: ComponentInfo) => {
  const message =
    errorField(error, "message") ?? (typeof error === "string" ? error : "");
  const pathname = sanitizedPathname();

  return {
    error_name: scrub(errorField(error, "name") ?? "UnknownError").slice(0, 80),
    error_message: scrub(message).slice(0, MAX_MESSAGE_LENGTH),
    error_stack: sanitizeStack(errorField(error, "stack")),
    component_stack: sanitizeStack(info.componentStack),
    page_path: pathname,
    $pathname: pathname,
    $current_url: `${window.location.origin}${pathname}`,
  };
};

const AUTOMATIC_URL_PROPERTY = /^\$(session_entry_|initial_|referr)/;

const withoutAutomaticUrls = <T extends object>(properties: T): T =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([key]) => !AUTOMATIC_URL_PROPERTY.test(key)
    )
  ) as T;

/**
 * Para o `before_send`: `$session_entry_url` e afins entram depois das
 * propriedades do evento e guardam a URL de entrada crua, então não dá para
 * sobrescrever no `capture`. Só toca este evento.
 */
export const stripBoundaryEventUrls = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (event?.event !== BOUNDARY_ERROR_EVENT) return event;

  return {
    ...event,
    properties: withoutAutomaticUrls(event.properties),
    ...(event.$set && { $set: withoutAutomaticUrls(event.$set) }),
    ...(event.$set_once && { $set_once: withoutAutomaticUrls(event.$set_once) }),
  };
};

export const reportBoundaryError = (
  error: unknown,
  info: ComponentInfo
): void => {
  // Falha de telemetria nunca pode derrubar a tela de erro.
  try {
    const properties = sanitizeBoundaryError(error, info);

    const deliver = (attempt: number) => {
      try {
        if (posthog.__loaded) {
          posthog.capture(BOUNDARY_ERROR_EVENT, properties, {
            send_instantly: true,
          });
        } else if (attempt < INIT_POLL_MAX_ATTEMPTS) {
          setTimeout(() => deliver(attempt + 1), INIT_POLL_INTERVAL_MS);
        }
      } catch {
        // ignorado de propósito
      }
    };

    deliver(0);
  } catch {
    // ignorado de propósito
  }
};
